import { useState } from "react";
import {
  Text,
  View,
  ScrollView,
  ActivityIndicator,
  Linking,
  Pressable,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { Link, Stack, useLocalSearchParams } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { Avatar } from "@/shared/components/ui/avatar";
import { BackButton } from "@/shared/components/ui/back-button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Pill } from "@/shared/components/ui/pill";
import { useCSSVariable } from "uniwind";
import { useAuthContext } from "@/features/auth/hooks/use-auth-context";
import { useProfile } from "@/features/profile/api/profile";
import { useFollowStatus } from "@/features/profile/api/followers";
import { FollowButton } from "@/features/profile/components/follow-button";
import { ProfileStatsBar } from "@/features/profile/components/profile-stats-bar";
import { ShareProfileButton } from "@/features/profile/components/share-profile-button";
import { UserEvents } from "@/features/profile/components/user-events";
import { FavoriteTags } from "@/features/profile/components/favorite-tags";
import { useUserEvents } from "@/features/events/api/event-participants";
import { AvatarPreviewModal } from "@/features/profile/components/avatar-preview-modal";

export default function PublicProfileScreen() {
  const { userId } = useLocalSearchParams<{
    userId: string;
  }>();
  const { session } = useAuthContext();
  const insets = useSafeAreaInsets();
  const { data: profile, isLoading, error } = useProfile(userId);
  const { data: userEvents } = useUserEvents(userId);
  const { data: followStatus } = useFollowStatus(userId);
  const foreground = useCSSVariable("--color-foreground") as string;
  const pink = useCSSVariable("--color-pink") as string;
  const isOwnProfile = userId === session?.user.id;
  const [previewVisible, setPreviewVisible] = useState(false);

  if (isLoading) {
    return (
      <View className="bg-background flex-1 items-center justify-center p-6">
        <ActivityIndicator
          size="large"
          className="text-brand"
          accessibilityLabel="Loading profile"
        />
      </View>
    );
  }

  if (error || !profile) {
    return (
      <View className="bg-background flex-1 items-center justify-center p-6">
        <Text
          className="text-destructive text-center text-base"
          accessibilityRole="alert"
        >
          Failed to load profile
        </Text>
      </View>
    );
  }

  const locationParts = [profile.city, profile.state].filter(Boolean);
  const location =
    locationParts.length > 0
      ? `${locationParts.join(" - ")}`
      : profile.country || "";

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <ScreenContainer style={{ paddingTop: 0 }}>
        <ScrollView
          contentContainerClassName="grow"
          className="flex-1"
          contentInsetAdjustmentBehavior="never"
        >
          <View className="bg-background flex-1">
            {/* Header with back button */}
            <View
              className="flex-row items-center justify-between px-4 pb-6"
              style={{ paddingTop: insets.top || 24 }}
            >
              <BackButton />
              <Text className="text-muted-foreground text-lg">
                @{profile.username}
              </Text>
              {isOwnProfile ? (
                <Link asChild href={"/user/settings"}>
                  <IconButton
                    shape="circle"
                    surface="transparent"
                    accessibilityLabel="Settings"
                  >
                    <Ionicons
                      name="settings-outline"
                      size={24}
                      color={foreground}
                    />
                  </IconButton>
                </Link>
              ) : (
                <Link asChild href={`/user/${userId}/report`}>
                  <IconButton
                    shape="circle"
                    surface="transparent"
                    accessibilityLabel="Denunciar usuário"
                  >
                    <Ionicons
                      name="alert-circle-outline"
                      size={28}
                      color={pink}
                    />
                  </IconButton>
                </Link>
              )}
            </View>

            {/* Profile Section */}
            <View className="px-6 pb-6">
              <View className="flex-row items-center gap-4">
                <Pressable
                  onPress={() => setPreviewVisible(true)}
                  disabled={!profile.avatarUrl}
                  accessibilityRole="imagebutton"
                  accessibilityLabel="Ampliar foto de perfil"
                >
                  <Avatar
                    url={profile.avatarUrl}
                    name={profile.displayName || profile.username}
                    size={88}
                  />
                </Pressable>
                <AvatarPreviewModal
                  visible={previewVisible}
                  onClose={() => setPreviewVisible(false)}
                  url={profile.avatarUrl}
                  name={profile.displayName || profile.username}
                />

                {/* Name, Location, Instagram */}
                <View className="flex-1 gap-1">
                  <Text
                    className="text-foreground font-display font-semibold"
                    style={{ fontSize: 20 }}
                  >
                    {profile.displayName || profile.username}
                  </Text>
                  {location && (
                    <Text className="text-muted-foreground text-sm">
                      {location}
                    </Text>
                  )}
                  <View className="mt-2 flex-row items-center gap-2">
                    {isOwnProfile ? (
                      <Link asChild href={"/user/edit"}>
                        <Pill
                          label="Editar"
                          variant="ghost"
                          tone="neutral"
                          selected
                          leftIcon={
                            <Ionicons
                              name="pencil"
                              size={14}
                              color={foreground}
                            />
                          }
                          minWidth={130}
                          accessibilityLabel="Editar perfil"
                        />
                      </Link>
                    ) : (
                      <FollowButton userId={userId} />
                    )}
                    <ShareProfileButton
                      userId={userId}
                      displayName={profile.displayName || profile.username}
                    />
                    {profile.instagramUsername &&
                      profile.shouldShowInstagram && (
                        <IconButton
                          size="sm"
                          shape="circle"
                          surface="tinted"
                          tone="pink"
                          accessibilityLabel={`Instagram de ${profile.displayName || profile.username}`}
                          accessibilityRole="link"
                          onPress={() =>
                            Linking.openURL(
                              `https://instagram.com/${profile.instagramUsername}`
                            )
                          }
                        >
                          <Ionicons
                            name="logo-instagram"
                            size={22}
                            color={pink}
                          />
                        </IconButton>
                      )}
                  </View>
                </View>
              </View>
            </View>

            <ProfileStatsBar
              followerCount={followStatus?.followerCount ?? 0}
              followingCount={followStatus?.followingCount ?? 0}
              eventCount={userEvents?.length ?? 0}
              userId={userId}
            />

            {/* User Events */}
            {userId && (
              <View className="mt-4 px-6">
                <UserEvents userId={userId} />
              </View>
            )}

            {/* Favorite Tags */}
            {userId && (
              <View className="mt-4 px-6">
                <FavoriteTags userId={userId} />
              </View>
            )}
          </View>
        </ScrollView>
      </ScreenContainer>
    </>
  );
}
